import * as THREE from 'three'
import type { ViewportRect } from '@/shared/types/core'
import type { RendererViewportRect, ViewRect } from '@/shared/types/view'

/**
 * 判断屏幕坐标点是否落在视图矩形内（含边界）。
 *
 * @param rect - 视图矩形，位置与尺寸单位像素
 * @param screen - 相对于画布左上角的屏幕像素坐标
 * @returns 点在矩形内或边界上返回 true
 */
export function containsScreenPoint(rect: ViewRect, screen: THREE.Vector2): boolean {
  return (
    screen.x >= rect.x &&
    screen.y >= rect.y &&
    screen.x <= rect.x + rect.width &&
    screen.y <= rect.y + rect.height
  )
}

/**
 * 将画布坐标转换为相对视口左上角的本地坐标。
 *
 * @param screen - 相对于画布左上角的屏幕像素坐标
 * @param viewport - 视口左上角相对画布的像素偏移
 * @returns 相对于视口左上角的像素坐标
 */
export function toViewportLocalPoint(
  screen: THREE.Vector2,
  viewport: { x: number; y: number }
): THREE.Vector2 {
  return new THREE.Vector2(screen.x - viewport.x, screen.y - viewport.y)
}

/**
 * 将屏幕像素坐标转换为视口 NDC，取值范围 [-1, 1]。
 *
 * @param screen - 相对于画布左上角的屏幕像素坐标
 * @param rect - 目标视口矩形
 * @returns NDC 坐标 Vector2，y 轴已翻转为向上为正
 */
export function screenToViewNDC(screen: THREE.Vector2, rect: ViewRect): THREE.Vector2 {
  const x = ((screen.x - rect.x) / rect.width) * 2 - 1
  const y = -(((screen.y - rect.y) / rect.height) * 2 - 1)
  return new THREE.Vector2(x === 0 ? 0 : x, y === 0 ? 0 : y)
}

/**
 * 屏幕像素坐标转 NDC，取值范围 [-1, 1]。
 *
 * @param screen - 相对于画布左上角的屏幕像素坐标
 * @param viewport - 视口矩形（位置与尺寸，单位像素）
 * @returns 归一化设备坐标 Vector2
 */
export function screenToNDC(screen: THREE.Vector2, viewport: ViewportRect): THREE.Vector2 {
  return screenToViewNDC(screen, viewport)
}

/**
 * 将以左上角为原点的视图矩形转换为 WebGLRenderer 要求的左下角原点视口矩形。
 *
 * @param rect - 输入视图矩形（左上角原点），单位像素
 * @param containerSize - 容器宽高，用于翻转 y 轴
 * @returns 可直接传给 renderer.setViewport 的视口矩形
 */
export function toRendererViewportRect(
  rect: ViewRect,
  containerSize: { width: number; height: number }
): RendererViewportRect {
  return {
    x: Math.round(rect.x),
    y: Math.round(containerSize.height - rect.y - rect.height),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  }
}
